// FileCard.js
// One uploaded file on a files list (Spec 2 section 6.3): drawn from the file
// document alone, never from its unit documents. The ledger on the file (one
// entry per unit) says enough to summarise its tables, and the batch document,
// while the file is being checked, says who it is checked with and how many
// questions the check raised.
//
// The card offers one primary action, chosen by the file's status:
//   parked            Resolve conflicts  (opens the review dialog, where the check's panel is)
//   AwaitingApproval  Approve file       (approves here)
//   Failed by the check  Fix             (opens the review dialog)
//   Reviewing         Review             (opens the review dialog)
//   anything else     Open               (opens the review dialog)
// and Download and Delete. A file refused at intake has none: nothing of it can be
// reviewed or changed, so its card shows the refusal, Download and Delete (Spec 1,
// "The refusal").
//
// The review dialog is hosted by the page, not here: a file changing status moves
// to another section of the list and unmounts this card, and the dialog must
// survive that. `onOpen(fileId)` asks the page to open it.
//
// `landingBlocked` says the table's claim reads `blocked` for this file's batch: its
// rows landed but the landing could not be finished (Spec 4 section 6). The card then
// says "Landing needs attention — contact support" in place of its status.
//
// A `file` with `isTemporary` is a placeholder the page shows while the file is
// uploading, before its document exists. `uploadFailed` on it means the upload did
// not register; `uploadUnconfirmed` means the page lost track of whether it did.
// Either way `onDismiss(file.id)` removes it.

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import { formatText } from '../../common/TextFormatterProvider';
import { formatDateOnly, formatTimestamp } from '../../../utils/timestamps';
import { countNoun } from '../../../utils/pluralise';
import { downloadFailureMessage } from '../../../services/api_utils';
import { approveFile, deleteFile, gestureFailureMessage } from '../../../services/cleaningGestures';
import { downloadFile } from '../../../services/fileDownloads';
import { deleteConfirmationText, fileDeleteBlock } from '../../../utils/fileDeletion';
import useFileBatch from '../../../hooks/useFileBatch';
import {
  FileStatusIcon,
  fileStatusColour,
  fileStatusLabel,
  LANDING_ATTENTION_MESSAGE,
  ledgerSummaryLine,
} from './ingestionStatusDisplay';

// The one primary action a file's status calls for, or null for a file refused
// at intake.
export function primaryActionOf(file) {
  const refusedAtIntake = file.verdicts.some((verdict) => verdict.level === 'fail' && verdict.process === 'intake');
  if (refusedAtIntake) return null;
  if (file.status === 'parked') {
    return { kind: 'open', label: 'Resolve conflicts', variant: 'contained', color: 'secondary' };
  }
  if (file.status === 'AwaitingApproval') {
    return { kind: 'approve', label: 'Approve file', variant: 'contained', color: 'primary' };
  }
  const failedByTheCheck = file.verdicts.some((verdict) => verdict.process === 'walk');
  if (file.status === 'Failed' && failedByTheCheck) {
    return { kind: 'open', label: 'Fix', variant: 'contained', color: 'primary' };
  }
  if (file.status === 'Reviewing') {
    return { kind: 'open', label: 'Review', variant: 'contained', color: 'primary' };
  }
  return { kind: 'open', label: 'Open', variant: 'outlined', color: 'primary' };
}

function PlaceholderCard({ file, onDismiss }) {
  const settled = file.uploadFailed || file.uploadUnconfirmed;
  return (
    <Card variant="outlined" sx={{ height: '100%', opacity: 0.7, border: '2px dashed', borderColor: 'primary.main' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" noWrap sx={{ flex: 1, minWidth: 0 }} title={file.name}>
            {file.name}
          </Typography>
          {settled && (
            <IconButton size="small" aria-label="Dismiss" onClick={() => onDismiss(file.id)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        {file.uploadFailed && (
          <Typography variant="caption" color="error">
            Upload failed — add this file again.
            {file.uploadError ? ` (${file.uploadError})` : ''}
          </Typography>
        )}
        {file.uploadUnconfirmed && (
          <Typography variant="caption" color="warning.main">
            Upload not confirmed — check this list before adding the file again.
            {file.uploadError ? ` (${file.uploadError})` : ''}
          </Typography>
        )}
        {!settled && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary">Uploading…</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function FileCard({
  file,
  companyId,
  tableId,
  tableName,
  profileNameById,
  selected = false,
  landingBlocked = false,
  onSelectChange,
  onOpen,
  onDismiss,
}) {
  const { batch, fileNames } = useFileBatch(companyId, tableId, file.isTemporary ? null : file);
  const [approving, setApproving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  // A failed approval or download, shown on the card until dismissed.
  const [actionError, setActionError] = useState(null);

  if (file.isTemporary) return <PlaceholderCard file={file} onDismiss={onDismiss} />;

  const action = primaryActionOf(file);
  const fails = file.verdicts.filter((verdict) => verdict.level === 'fail');
  const profileLabel = profileNameById?.[file.data_profile_id] ?? formatText(file.data_profile_id);
  const mates = batch ? batch.files.filter((id) => id !== file.id).map((id) => fileNames[id]).filter(Boolean) : [];
  const deleteBlock = fileDeleteBlock(file, batch);
  const deleteFailed = Boolean(file.deletion?.failed);

  const handlePrimary = async () => {
    if (action.kind === 'open') {
      onOpen(file.id);
      return;
    }
    setApproving(true);
    setActionError(null);
    try {
      await approveFile(companyId, tableId, file.id);
    } catch (error) {
      setActionError(gestureFailureMessage(error));
    } finally {
      setApproving(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setActionError(null);
    try {
      const { savedCount, failed } = await downloadFile(companyId, tableId, file);
      if (failed.length > 0) setActionError(downloadFailureMessage(failed, savedCount));
    } catch (error) {
      console.error('Error downloading a file:', error);
      setActionError(downloadFailureMessage([file.name]));
    } finally {
      setDownloading(false);
    }
  };

  const openDeleteConfirmation = () => {
    setDeleteError(null);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteFile(companyId, tableId, file.id);
      setDeleteOpen(false);
    } catch (error) {
      setDeleteError(gestureFailureMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            {onSelectChange && (
              <Checkbox
                size="small"
                checked={selected}
                // The file id rides along so a page can pass one handler for every card.
                onChange={(event) => onSelectChange(event.target.checked, file.id)}
                inputProps={{ 'aria-label': `Select ${file.name}` }}
                sx={{ p: 0.25 }}
              />
            )}
            {landingBlocked ? <ErrorIcon color="error" sx={{ fontSize: 18 }} /> : <FileStatusIcon status={file.status} />}
            <Typography variant="subtitle1" noWrap sx={{ flex: 1, minWidth: 0 }} title={file.name}>
              {file.name}
            </Typography>
            {landingBlocked ? (
              <Chip size="small" color="error" label={LANDING_ATTENTION_MESSAGE} />
            ) : (
              <Chip size="small" color={fileStatusColour(file.status)} label={fileStatusLabel(file.status)} />
            )}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {profileLabel} · uploaded {formatTimestamp(file.created_at)}
            {file.date ? ` · reporting ${formatDateOnly(file.date)}` : ''}
          </Typography>

          <Typography variant="body2" sx={{ mt: 1 }}>{ledgerSummaryLine(file)}</Typography>

          {fails.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 1 }}>
              <ErrorIcon color="error" sx={{ fontSize: 16, mt: '2px' }} />
              <Typography variant="body2" color="error">
                {fails[0].message}
                {fails.length > 1 ? ` · and ${fails.length - 1} more` : ''}
              </Typography>
            </Box>
          )}

          {deleteFailed && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>{file.deletion.failed}</Typography>
          )}

          {mates.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Checked together with {mates.join(', ')}
            </Typography>
          )}
          {batch && batch.status === 'parked' && (
            <Typography variant="caption" color="secondary" sx={{ display: 'block' }}>
              {countNoun(batch.open_questions, 'question')} to answer
              {batch.answered_questions > 0 ? ` · ${batch.answered_questions} answered` : ''}
            </Typography>
          )}

          {actionError && (
            <Alert severity="error" sx={{ mt: 1 }} onClose={() => setActionError(null)}>{actionError}</Alert>
          )}
        </CardContent>

        <CardActions sx={{ px: 2, pb: 1.5, pt: 0 }}>
          {action && (
            <Button
              size="small"
              variant={action.variant}
              color={action.color}
              onClick={handlePrimary}
              disabled={approving}
            >
              {approving ? <CircularProgress size={16} color="inherit" /> : action.label}
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            startIcon={downloading ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
            onClick={handleDownload}
            disabled={downloading}
          >
            Download
          </Button>
          {/* A disabled button fires no pointer events, so its reason is on a wrapper. */}
          <Tooltip title={deleteBlock}>
            <span>
              <Button size="small" color="error" onClick={openDeleteConfirmation} disabled={Boolean(deleteBlock)}>
                {deleteFailed ? 'Try again' : 'Delete'}
              </Button>
            </span>
          </Tooltip>
        </CardActions>
      </Card>

      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
        <DialogTitle>Delete this file?</DialogTitle>
        <DialogContent>
          <DialogContentText>{deleteConfirmationText(file, tableName)}</DialogContentText>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// A list can hold dozens of these cards, and every write to one file redraws the
// list. A card is redrawn only when one of its own inputs changed, so a page that
// keeps unchanged inputs identical redraws just the file that moved.
export default React.memo(FileCard);
